(() => {
  const instances = new Map();
  const views = ["welcome", "practice", "space", "plan", "inquiry", "complete"];
  const profiles = {
    moving: {
      label: "Moving from a gym",
      note: "Your client relationships, rates and programming stay yours.",
    },
    building: {
      label: "Building my own base",
      note: "Explore a regular base for your independent coaching business.",
    },
    exploring: {
      label: "Testing the fit",
      note: "Start by understanding the room. You can compare every option.",
    },
  };
  const plans = {
    pro: { label: "Founding Pro", price: "$700 / month", rent: 700 },
    titanium: { label: "Founding Titanium", price: "$500 / month", rent: 500 },
    flex: { label: "Flex trial", price: "$30 / block · then $45", rent: null },
  };
  const storageKey = "monarch.trainer-path.v1";
  const money = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  const owns = (object, key) =>
    typeof key === "string" && Object.hasOwn(object, key);
  const clamp = (value, min, max, fallback) =>
    (typeof value === "number" ||
      (typeof value === "string" && value.trim() !== "")) &&
    Number.isFinite(Number(value))
      ? Math.min(max, Math.max(min, Number(value)))
      : fallback;

  function mount(root) {
    if (instances.has(root)) return;
    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => Array.from(root.querySelectorAll(selector));
    const form = root.closest("form") || q("form");
    if (!form || !q('[data-mj-screen="welcome"]')) return;
    const originalNoValidate = form.noValidate;
    const originalRangeStates = qa(
      "[data-mj-rate], [data-mj-sessions], [data-mj-profile], [data-mj-plan]",
    ).map((input) => [input, input.disabled]);
    const scrollMode = root.dataset.mjMode === "scroll";
    // Keep native validation ahead of Shopify's CAPTCHA submit interception.
    // The wizard validates after revealing its otherwise-hidden fields.
    form.noValidate = !scrollMode;
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const motionAllowed = () =>
      gsap && !reduced.matches && root.dataset.mjMotion !== "false";
    const controller = new AbortController();
    const on = (target, type, callback, options = {}) =>
      target?.addEventListener(type, callback, {
        ...options,
        signal: controller.signal,
      });
    const context = gsap?.context(() => {}, root);
    let scrollMedia;
    let chapterObserver;
    let inquiryObserver;
    let chapterFrame = 0;
    let mediaFrame = 0;
    let progressFrame = 0;
    let alive = true;
    let transition;
    let busy = false;
    let active = true;
    let view = "welcome";
    let reached = 1;
    let submitted = false;
    let needsChoiceReview = false;
    let isPreview = root.dataset.mjPreview === "true";
    let state = {
      profile: null,
      plan: "pro",
      rate: 65,
      sessions: 15,
      resume: "practice",
    };
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      if (
        saved &&
        (owns(profiles, saved.profile) ||
          (scrollMode && saved.profile === null && owns(plans, saved.plan)))
      ) {
        state = {
          profile: saved.profile,
          plan: owns(plans, saved.plan) ? saved.plan : "pro",
          rate: clamp(saved.rate, 30, 200, 65),
          sessions: Math.round(clamp(saved.sessions, 1, 40, 15)),
          resume: ["practice", "space", "plan", "inquiry"].includes(
            saved.resume,
          )
            ? saved.resume
            : "practice",
        };
        reached = Math.max(1, views.indexOf(state.resume));
        if (!scrollMode)
          q("[data-mj-start]").firstChild.textContent = "Continue my path ";
      }
    } catch {
      /* Functional choices are optional; personal form fields are never stored. */
    }
    const returned = q("[data-mj-form-state]").dataset.mjFormState;
    if (returned === "error") {
      const match =
        /^MONARCH TRAINER PATH\r?\nPractice: (.+)\r?\nSeat: (.+)\r?\nSession rate \(USD\): (\d+(?:\.\d+)?)\r?\nSessions per week: (\d+)\r?\n\r?\nTRAINING NEEDS\r?\n([\s\S]*)$/.exec(
          q("#mj-message").value,
        );
      const profile =
        match &&
        Object.keys(profiles).find((key) => profiles[key].label === match[1]);
      const plan =
        match &&
        Object.keys(plans).find((key) => plans[key].label === match[2]);
      if (profile && plan) {
        state = {
          profile,
          plan,
          rate: clamp(match[3], 30, 200, 65),
          sessions: Math.round(clamp(match[4], 1, 40, 15)),
          resume: "inquiry",
        };
        q("#mj-message").value = match[5];
      } else needsChoiceReview = !state.profile;
    }
    const save = () => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        /* Continue in memory. */
      }
    };
    const animate = (callback) =>
      context ? context.add(callback) : callback();
    const clearMotion = () => {
      transition?.kill();
      transition = null;
      busy = false;
      root.removeAttribute("aria-busy");
      qa(
        "[data-mj-screen], [data-mj-reveal], [data-mj-hero-image], [data-mj-space-image], [data-mj-zone-panel], [data-mj-progress]",
      ).forEach((element) => {
        if (gsap) {
          gsap.killTweensOf(element);
          if (!element.matches("[data-mj-progress]"))
            gsap.set(element, { clearProps: "opacity,visibility,transform" });
        }
      });
    };
    const update = () => {
      qa("[data-mj-profile]").forEach((input) => {
        input.checked = input.value === state.profile;
      });
      qa("[data-mj-plan]").forEach((input) => {
        input.checked = input.value === state.plan;
      });
      const profile = profiles[state.profile];
      const plan = plans[state.plan];
      if (q("[data-ms-calculator-label]"))
        q("[data-ms-calculator-label]").textContent =
          `${plan.label.toUpperCase()} / ${plan.rent === null ? "PER BLOCK" : `${money(plan.rent)} PER MONTH`}`;
      q("[data-mj-profile-note]").textContent =
        profile?.note || "Choose the starting point that sounds like you.";
      q("[data-mj-rate]").value = String(state.rate);
      q("[data-mj-sessions]").value = String(state.sessions);
      q("[data-mj-rate-output]").textContent = money(state.rate);
      q("[data-mj-sessions-output]").textContent = String(state.sessions);
      q("[data-mj-rate]").setAttribute(
        "aria-valuetext",
        `${money(state.rate)} per session`,
      );
      q("[data-mj-sessions]").setAttribute(
        "aria-valuetext",
        `${state.sessions} sessions per week`,
      );
      q("[data-mj-rate]").style.setProperty(
        "--ms-fill",
        `${((state.rate - 30) / 170) * 100}%`,
      );
      q("[data-mj-sessions]").style.setProperty(
        "--ms-fill",
        `${((state.sessions - 1) / 39) * 100}%`,
      );
      const gross = state.rate * state.sessions * 4.33;
      q("[data-mj-gross]").textContent = money(gross);
      q("[data-mj-rent]").textContent =
        plan.rent === null ? "Per block" : "−" + money(plan.rent);
      q("[data-mj-net]").textContent =
        plan.rent === null ? "—" : money(gross - plan.rent);
      if (q("[data-ms-meter]"))
        q("[data-ms-meter]").style.transform =
          `scaleX(${plan.rent === null ? 0 : Math.max(0, (gross - plan.rent) / gross)})`;
      q("[data-mj-resident-numbers]").hidden = state.plan === "flex";
      q("[data-mj-flex-note]").hidden = state.plan !== "flex";
      q("[data-mj-summary-plan]").textContent = plan.label;
      q("[data-mj-summary-price]").textContent = plan.price;
      q("[data-mj-summary-profile]").textContent =
        profile?.label || "Exploring my options";
      const scenario = `${money(state.rate)} per session · ${state.sessions} per week`;
      q("[data-mj-summary-scenario]").textContent = scenario;
      q("[data-mj-summary-scenario-row]").hidden = state.plan === "flex";
      q("[data-mj-field-profile]").value =
        profile?.label || "Exploring my options";
      q("[data-mj-field-plan]").value = plan.label;
      q("[data-mj-field-scenario]").value =
        state.plan === "flex"
          ? "Flex: up to 12 paid trial blocks over 30 days; then $45/block."
          : `${scenario}; ${money(gross - plan.rent)} monthly after seat, before other expenses/taxes. Illustrative, not a forecast.`;
      q("[data-mj-field-body]").value =
        `MONARCH TRAINER PATH\nPractice: ${profile?.label || profiles.exploring.label}\nSeat: ${plan.label}\nSession rate (USD): ${state.rate}\nSessions per week: ${state.sessions}\n\nTRAINING NEEDS\n${q("#mj-message").value}`;
    };
    const chapterLabels = {
      welcome: "Welcome",
      practice: "Your practice",
      space: "Your space",
      plan: "Your numbers",
      inquiry: "Your inquiry",
      complete: "Inquiry complete",
    };
    function setChapter(next) {
      if (!views.includes(next)) return;
      view = next;
      root.dataset.view = next;
      qa("[data-ms-nav] [data-mj-goto], [data-ms-nav][data-mj-goto]").forEach(
        (link) => {
          if (link.dataset.mjGoto === next)
            link.setAttribute("aria-current", "location");
          else link.removeAttribute("aria-current");
        },
      );
      if (q("[data-mj-step-label]"))
        q("[data-mj-step-label]").textContent = chapterLabels[next];
    }
    function renderScrollView(next, focus = true, resetScroll = true) {
      setChapter(next);
      const complete = next === "complete";
      root.dataset.mjComplete = String(complete);
      qa("[data-mj-screen]").forEach((panel) => {
        panel.hidden = complete
          ? panel.dataset.mjScreen !== "complete"
          : panel.dataset.mjScreen === "complete";
      });
      qa("[data-mj-rail], [data-mj-controls], [data-mj-exit]").forEach(
        (element) => {
          element.hidden = true;
        },
      );
      qa(
        "[data-ms-nav], [data-ms-dock], .ms-mobile-dock, .ms-chapter-nav",
      ).forEach((element) => {
        element.hidden = complete;
      });
      update();
      const panel = q(`[data-mj-screen="${next}"]`);
      if (focus) {
        panel?.querySelector("h1, h2")?.focus({ preventScroll: true });
        if (q("[data-mj-live]"))
          q("[data-mj-live]").textContent = chapterLabels[next];
      }
      if (resetScroll)
        panel?.scrollIntoView?.({
          block: "start",
          behavior: motionAllowed() ? "smooth" : "instant",
        });
      if (next !== "complete" && next !== "welcome") {
        state.resume = next;
        save();
      }
      if (complete) scrollMedia?.revert();
      requestProgress();
    }
    function requestProgress() {
      if (progressFrame || !alive) return;
      progressFrame = window.requestAnimationFrame(() => {
        progressFrame = 0;
        if (!alive || !active) return;
        const doc = document.documentElement;
        const range = Math.max(1, doc.scrollHeight - window.innerHeight);
        const amount = Math.min(1, Math.max(0, window.scrollY / range));
        qa(
          "[data-mj-progress], [data-ms-progress], [data-ms-page-progress]",
        ).forEach((bar) => {
          bar.style.transform = `scaleX(${amount})`;
        });
      });
    }
    function setupScrollMotion() {
      scrollMedia?.revert();
      scrollMedia = null;
      if (gsap && !motionAllowed())
        qa("[data-mj-zone-panel]").forEach((panel) => {
          gsap.killTweensOf(panel);
          gsap.set(panel, { clearProps: "opacity,transform" });
        });
      if (!alive || !gsap || !ScrollTrigger || !motionAllowed() || submitted)
        return;
      gsap.registerPlugin(ScrollTrigger);
      scrollMedia = gsap.matchMedia();
      scrollMedia.add(
        {
          desktop: "(min-width: 1024px)",
          mobile: "(max-width: 1023px)",
          reduced: "(prefers-reduced-motion: reduce)",
        },
        (media) => {
          if (media.conditions.reduced || !motionAllowed()) return;
          const distance = media.conditions.desktop ? 38 : 18;
          qa("[data-mj-screen]:not([data-mj-screen='complete'])").forEach(
            (panel) => {
              const welcome = panel.dataset.mjScreen === "welcome";
              const scene = panel.querySelector("[data-ms-scene]");
              const image = scene?.querySelector("img");
              if (image)
                gsap.fromTo(
                  image,
                  { y: distance },
                  {
                    y: -distance,
                    ease: "none",
                    scrollTrigger: {
                      trigger: scene,
                      start: welcome ? "top top" : "top bottom",
                      end: "bottom top",
                      scrub: 0.45,
                      invalidateOnRefresh: true,
                    },
                  },
                );
              const text = Array.from(
                panel.querySelectorAll("[data-mj-reveal]"),
              ).filter((element) => !welcome || !element.closest("h1"));
              if (welcome && panel.querySelector("h1"))
                text.splice(1, 0, panel.querySelector("h1"));
              if (text.length)
                gsap.fromTo(
                  text,
                  { y: welcome ? 26 : 20, opacity: welcome ? 1 : 0.72 },
                  {
                    y: 0,
                    opacity: 1,
                    duration: 0.75,
                    stagger: 0.075,
                    ease: "power3.out",
                    clearProps: "opacity,transform",
                    immediateRender: false,
                    ...(welcome
                      ? {}
                      : {
                          scrollTrigger: {
                            trigger: panel,
                            start: "top 90%",
                            once: true,
                          },
                        }),
                  },
                );
            },
          );
        },
        root,
      );
    }
    function observeChapters() {
      if (window.IntersectionObserver) {
        const visible = new Map();
        const connect = () => {
          chapterObserver?.disconnect();
          visible.clear();
          chapterObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) visible.set(entry.target, entry);
                else visible.delete(entry.target);
              });
              if (submitted) return;
              const current = Array.from(visible.values()).sort(
                (a, b) =>
                  Math.abs(a.boundingClientRect.top) -
                  Math.abs(b.boundingClientRect.top),
              )[0];
              if (current) setChapter(current.target.dataset.mjScreen);
              requestProgress();
            },
            {
              // Percentage root margins resolve against width, not height.
              rootMargin: `-${Math.round(window.innerHeight * 0.15)}px 0px -${Math.round(window.innerHeight * 0.55)}px 0px`,
              threshold: 0,
            },
          );
          qa("[data-mj-screen]").forEach((panel) =>
            chapterObserver.observe(panel),
          );
        };
        connect();
        on(
          window,
          "resize",
          () => {
            if (chapterFrame) return;
            chapterFrame = window.requestAnimationFrame(() => {
              chapterFrame = 0;
              if (alive) connect();
            });
          },
          { passive: true },
        );
        inquiryObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries.find(
              (item) => item.target.dataset.mjScreen === "inquiry",
            );
            if (entry)
              root.dataset.msInquiryVisible = String(entry.isIntersecting);
          },
          { rootMargin: "0px", threshold: 0 },
        );
        inquiryObserver.observe(q('[data-mj-screen="inquiry"]'));
      }
      on(window, "scroll", requestProgress, { passive: true });
      on(window, "resize", requestProgress, { passive: true });
      qa("[data-ms-scene] img").forEach((image) => {
        on(image, "load", () => {
          if (scrollMedia) ScrollTrigger.refresh(true);
          requestProgress();
        });
      });
      document.fonts?.ready.then(() => {
        if (alive && scrollMedia) ScrollTrigger.refresh(true);
        requestProgress();
      });
      requestProgress();
    }
    function renderView(next, focus = true, resetScroll = true) {
      if (scrollMode) {
        renderScrollView(next, focus, resetScroll);
        return;
      }
      view = next;
      root.dataset.view = next;
      qa("[data-mj-screen]").forEach((panel) => {
        panel.hidden = panel.dataset.mjScreen !== next;
      });
      const step = views.indexOf(next);
      const guided = step > 0 && step < 5;
      reached = Math.max(reached, Math.min(step, 4));
      q("[data-mj-rail]").hidden = !guided;
      q("[data-mj-controls]").hidden = !guided;
      q("[data-mj-progress-wrap]").hidden = !guided;
      q("[data-mj-next]").hidden = next === "inquiry";
      const labels = {
        practice: "Your practice",
        space: "Your space",
        plan: "Your plan",
        inquiry: "Let's talk",
      };
      q("[data-mj-step-label]").textContent =
        `${String(step).padStart(2, "0")} / ${labels[next] || ""}`;
      qa("[data-mj-goto]").forEach((button) => {
        const index = views.indexOf(button.dataset.mjGoto);
        button.disabled = index > reached;
        if (button.dataset.mjGoto === next)
          button.setAttribute("aria-current", "step");
        else button.removeAttribute("aria-current");
        button.dataset.complete = String(index < step);
      });
      const nextLabels = {
        practice: "Explore the space",
        space: "Build my plan",
        plan: "Review my inquiry",
      };
      q("[data-mj-next]").firstChild.textContent =
        (nextLabels[next] || "Continue") + " ";
      q("[data-mj-hint]").textContent =
        next === "inquiry"
          ? "A CONVERSATION. NO COMMITMENT."
          : "YOUR BUSINESS. YOUR PACE.";
      q("[data-mj-live]").textContent = guided
        ? `Step ${step} of 4. ${labels[next]}.`
        : "";
      if (guided) {
        state.resume = next;
        save();
      }
      update();
      if (focus)
        q(
          `[data-mj-screen="${next}"] h2, [data-mj-screen="${next}"] h1`,
        )?.focus({ preventScroll: true });
      if (resetScroll) {
        q("[data-mj-stage]").scrollTop = 0;
        window.scrollTo({ top: 0, behavior: "instant" });
      }
      if (motionAllowed())
        animate(() =>
          gsap.to(q("[data-mj-progress]"), {
            scaleX: Math.min(step / 4, 1),
            duration: 0.6,
            ease: "power3.out",
          }),
        );
      else
        q("[data-mj-progress]").style.transform =
          `scaleX(${Math.min(step / 4, 1)})`;
    }
    function enter(next, options = {}) {
      if (
        !active ||
        busy ||
        !views.includes(next) ||
        (next === "complete" && !submitted)
      )
        return;
      if (scrollMode) {
        renderScrollView(
          next,
          options.focus !== false,
          options.scroll !== false,
        );
        if (options.history !== false) {
          const url = new URL(window.location.href);
          url.hash = `mj-${next}`;
          history[options.replace ? "replaceState" : "pushState"](
            { ...(history.state || {}), monarchJourney: next },
            "",
            url,
          );
        }
        return;
      }
      if (views.indexOf(next) > 1 && next !== "complete" && !state.profile) {
        q("[data-mj-profile-note]").textContent =
          "Choose a starting point to continue.";
        if (view !== "practice") renderView("practice");
        q("[data-mj-profile]").focus();
        return;
      }
      clearMotion();
      if (next === "inquiry" && view === "plan") needsChoiceReview = false;
      const old = q(`[data-mj-screen="${view}"]`);
      const direction = views.indexOf(next) < views.indexOf(view) ? -1 : 1;
      const commit = () => {
        renderView(next);
        if (options.history !== false) {
          const entry = {
            ...(history.state || {}),
            monarchJourney: next,
            monarchJourneyIndex: options.replace
              ? 0
              : (history.state?.monarchJourneyIndex || 0) + 1,
          };
          const url = new URL(window.location.href);
          url.hash = next === "welcome" ? "" : `mj-${next}`;
          history[options.replace ? "replaceState" : "pushState"](
            entry,
            "",
            url,
          );
        }
      };
      if (!motionAllowed() || options.immediate) {
        clearMotion();
        commit();
        return;
      }
      busy = true;
      root.setAttribute("aria-busy", "true");
      animate(() => {
        transition = gsap.timeline({
          onComplete: () => {
            busy = false;
            root.removeAttribute("aria-busy");
            transition = null;
          },
        });
        transition
          .to(old, {
            x: -24 * direction,
            opacity: 0,
            duration: 0.18,
            ease: "power2.in",
          })
          .call(() => {
            gsap.set(old, { clearProps: "opacity,transform" });
            commit();
          })
          .fromTo(
            q(`[data-mj-screen="${next}"]`),
            { x: 36 * direction, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.55,
              ease: "power3.out",
              clearProps: "opacity,transform",
              immediateRender: false,
            },
          )
          .fromTo(
            qa(`[data-mj-screen="${next}"] [data-mj-reveal]`),
            { y: 24, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              stagger: 0.075,
              ease: "power3.out",
              clearProps: "opacity,transform",
              immediateRender: false,
            },
            "<0.05",
          );
      });
    }
    const showZone = (name, focus = false) => {
      if (!["floor", "equipment", "recovery"].includes(name)) return;
      qa("[data-mj-zone]").forEach((button) => {
        const selected = button.dataset.mjZone === name;
        button.setAttribute("aria-selected", String(selected));
        button.tabIndex = selected ? 0 : -1;
        if (selected && focus) button.focus();
      });
      qa("[data-mj-zone-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.mjZonePanel !== name;
      });
      if (motionAllowed())
        animate(() => {
          gsap.fromTo(
            q(`[data-mj-zone-panel="${name}"]`),
            { y: 18, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.4,
              overwrite: true,
              clearProps: "opacity,transform",
            },
          );
          if (!scrollMode)
            gsap.to(q("[data-mj-space-image]"), {
              scale: name === "floor" ? 1 : name === "equipment" ? 1.1 : 1.04,
              xPercent: name === "equipment" ? -2 : 0,
              duration: 0.9,
              ease: "power3.out",
              overwrite: true,
            });
        });
      if (scrollMode && scrollMedia) ScrollTrigger.refresh(true);
    };
    on(root, "click", (event) => {
      const target = event.target.closest("button,a");
      if (!target || !root.contains(target)) return;
      if (
        scrollMode &&
        target.matches("a") &&
        (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      )
        return;
      if (target.matches("[data-mj-start]")) {
        event.preventDefault();
        enter(
          scrollMode ? "practice" : state.profile ? state.resume : "practice",
        );
      }
      if (target.matches("[data-mj-skip]")) {
        event.preventDefault();
        if (!scrollMode) state.profile ||= "exploring";
        reached = 3;
        update();
        enter("plan");
      }
      if (target.matches("[data-mj-next]"))
        enter(views[views.indexOf(view) + 1]);
      if (target.matches("[data-mj-back]")) {
        if (busy) return;
        if ((history.state?.monarchJourneyIndex || 0) > 0) history.back();
        else
          enter(views[Math.max(0, views.indexOf(view) - 1)], { replace: true });
      }
      if (target.matches("[data-mj-goto]") && !target.disabled) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        event.preventDefault();
        enter(target.dataset.mjGoto);
      }
      if (target.matches("[data-mj-edit]")) {
        event.preventDefault();
        enter("plan");
        if (scrollMode) needsChoiceReview = false;
      }
      if (target.matches("[data-mj-zone]")) showZone(target.dataset.mjZone);
      if (target.matches("[data-mj-restart]")) {
        state = {
          profile: null,
          plan: "pro",
          rate: 65,
          sessions: 15,
          resume: "practice",
        };
        reached = 1;
        submitted = false;
        needsChoiceReview = false;
        form.reset();
        q('[data-mj-screen="inquiry"]')
          .querySelectorAll('input:not([type="hidden"]), textarea')
          .forEach((input) => {
            if (input.type === "checkbox") input.checked = false;
            else input.value = "";
          });
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          /* Optional storage. */
        }
        q("[data-mj-start]").firstChild.textContent = scrollMode
          ? "Explore my fit "
          : "Build my path ";
        enter("welcome", { replace: true });
        if (scrollMode) setupScrollMotion();
      }
      if (target.matches("[data-mj-exit]") && !scrollMode) {
        clearMotion();
        active = false;
        root.hidden = true;
        document.body.classList.remove("mj-active");
        const fallback = document.querySelector("[data-mj-site-fallback]");
        fallback?.querySelector("h2")?.setAttribute("tabindex", "-1");
        fallback?.querySelector("h2")?.focus();
        const url = new URL(location.href);
        url.hash = "";
        history.replaceState(
          { ...(history.state || {}), monarchJourney: null },
          "",
          url,
        );
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    });
    on(root, "change", (event) => {
      let changed = false;
      if (
        event.target.matches("[data-mj-profile]") &&
        owns(profiles, event.target.value)
      ) {
        state.profile = event.target.value;
        changed = true;
        update();
        save();
      }
      if (
        event.target.matches("[data-mj-plan]") &&
        owns(plans, event.target.value)
      ) {
        state.plan = event.target.value;
        changed = true;
        needsChoiceReview = false;
        update();
        save();
      }
      if (scrollMode && changed) {
        if (scrollMedia) ScrollTrigger.refresh(true);
        requestProgress();
      }
    });
    on(root, "input", (event) => {
      if (event.target.matches("[data-mj-rate]"))
        state.rate = clamp(event.target.value, 30, 200, 65);
      else if (event.target.matches("[data-mj-sessions]"))
        state.sessions = Math.round(clamp(event.target.value, 1, 40, 15));
      else if (event.target.matches("#mj-message")) {
        update();
        return;
      } else return;
      update();
      save();
    });
    on(root, "keydown", (event) => {
      if (
        !event.target.matches("[data-mj-zone]") ||
        !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
      )
        return;
      event.preventDefault();
      const names = ["floor", "equipment", "recovery"];
      const index = names.indexOf(event.target.dataset.mjZone);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? 2
            : (index + (event.key === "ArrowRight" ? 1 : 2)) % 3;
      showZone(names[next], true);
    });
    on(window, "popstate", (event) => {
      if (scrollMode) {
        const target = location.hash.slice(1).replace(/^mj-/, "") || "welcome";
        if (active && views.includes(target))
          enter(target, { history: false, focus: false });
        return;
      }
      if (active && views.includes(event.state?.monarchJourney)) {
        clearMotion();
        enter(event.state.monarchJourney, { history: false, immediate: true });
      }
    });
    if (scrollMode)
      on(window, "hashchange", () => {
        const target = location.hash.slice(1).replace(/^mj-/, "") || "welcome";
        if (active && views.includes(target) && target !== view)
          enter(target, { history: false, focus: false });
      });
    on(reduced, "change", () => {
      if (active) {
        if (scrollMode) {
          const position = { top: window.scrollY, left: window.scrollX };
          const restorePosition = () => {
            if (
              alive &&
              (window.scrollY !== position.top ||
                window.scrollX !== position.left)
            )
              window.scrollTo({ ...position, behavior: "instant" });
          };
          setupScrollMotion();
          // Reverting ScrollTrigger can temporarily restore its cached scroll.
          restorePosition();
          if (mediaFrame) window.cancelAnimationFrame(mediaFrame);
          mediaFrame = window.requestAnimationFrame(() => {
            mediaFrame = 0;
            restorePosition();
          });
          return;
        }
        clearMotion();
        renderView(view, false, false);
      }
    });
    on(form, "submit", (event) => {
      if (event.defaultPrevented) return;
      if (scrollMode && !state.profile) {
        event.preventDefault();
        enter("practice");
        q("[data-mj-profile-note]").textContent =
          "Choose your starting point before sending an inquiry.";
        q("[data-mj-profile]").focus({ preventScroll: true });
        return;
      }
      if (!scrollMode && view !== "inquiry") {
        event.preventDefault();
        enter("inquiry");
        return;
      }
      if (needsChoiceReview) {
        event.preventDefault();
        enter("plan");
        if (scrollMode) {
          needsChoiceReview = false;
          if (q("[data-mj-live]"))
            q("[data-mj-live]").textContent =
              "Review your seat and numbers, then return to your inquiry.";
        }
        return;
      }
      update();
      if (!form.checkValidity()) {
        event.preventDefault();
        if (scrollMode) enter("inquiry");
        form.reportValidity();
        return;
      }
      if (!isPreview) return; // Shopify alone confirms a real inquiry.
      event.preventDefault();
      submitted = true;
      q("[data-mj-complete-kicker]").textContent =
        "PREVIEW COMPLETE / NOTHING SENT";
      q("[data-mj-complete-copy]").textContent =
        "Your path is ready. This is a local preview, so no inquiry has been sent. On the connected site, this step follows Shopify confirming receipt.";
      enter("complete");
    });
    if (isPreview) {
      q("[data-mj-preview-note]").hidden = false;
      q("[data-mj-submit]").firstChild.textContent = "Preview my inquiry ";
    }
    root.dataset.mjInitialized = "true";
    root.dataset.view = "welcome";
    if (q("[data-mj-exit]")) q("[data-mj-exit]").hidden = scrollMode;
    if (q("[data-mj-skip]")) q("[data-mj-skip]").hidden = false;
    if (q(".mj-fallback-link")) q(".mj-fallback-link").hidden = true;
    document.body.classList.add(scrollMode ? "mj-scroll-active" : "mj-active");
    if (scrollMode)
      qa(
        "[data-mj-rate], [data-mj-sessions], [data-mj-profile], [data-mj-plan]",
      ).forEach((input) => {
        input.disabled = false;
      });
    update();
    if (returned === "success") {
      submitted = true;
      renderView("complete");
    } else if (returned === "error") {
      if (!scrollMode) state.profile ||= "exploring";
      reached = 4;
      renderView("inquiry");
      if (q("[data-mj-summary-postback]"))
        q("[data-mj-summary-postback]").hidden = false;
      q("[data-mj-server-error]")?.focus();
    } else if (scrollMode) {
      const target = location.hash.slice(1).replace(/^mj-/, "");
      renderScrollView(
        views.includes(target) && target !== "complete" ? target : "welcome",
        false,
        views.includes(target) && target !== "complete",
      );
    } else {
      renderView("welcome", false);
      if (motionAllowed())
        animate(() => {
          transition = gsap.timeline({
            onComplete: () => {
              transition = null;
            },
          });
          transition
            .fromTo(
              qa('[data-mj-screen="welcome"] [data-mj-reveal]'),
              { y: 38 },
              {
                y: 0,
                duration: 0.85,
                stagger: 0.09,
                ease: "power3.out",
                clearProps: "transform",
              },
            )
            .fromTo(
              q("[data-mj-hero-image]"),
              { scale: 1.14 },
              {
                scale: 1,
                duration: 2.1,
                ease: "power2.out",
                clearProps: "transform",
              },
              0,
            );
        });
    }
    history.replaceState(
      {
        ...(history.state || {}),
        monarchJourney: view,
        monarchJourneyIndex: 0,
      },
      "",
      location.href,
    );
    if (scrollMode) {
      setupScrollMotion();
      observeChapters();
    }
    instances.set(root, () => {
      alive = false;
      controller.abort();
      chapterObserver?.disconnect();
      inquiryObserver?.disconnect();
      if (chapterFrame) window.cancelAnimationFrame(chapterFrame);
      if (mediaFrame) window.cancelAnimationFrame(mediaFrame);
      if (progressFrame) window.cancelAnimationFrame(progressFrame);
      scrollMedia?.revert();
      transition?.kill();
      context?.revert();
      form.noValidate = originalNoValidate;
      if (scrollMode)
        originalRangeStates.forEach(([input, disabled]) => {
          input.disabled = disabled;
        });
      document.body.classList.remove(
        scrollMode ? "mj-scroll-active" : "mj-active",
      );
      root.removeAttribute("data-mj-initialized");
      root.removeAttribute("data-ms-inquiry-visible");
    });
  }
  const initialize = (scope = document) => {
    if (scope.matches?.("[data-monarch-journey]")) mount(scope);
    scope.querySelectorAll("[data-monarch-journey]").forEach(mount);
  };
  initialize();
  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    for (const [root, cleanup] of instances)
      if (event.target.contains(root)) {
        cleanup();
        instances.delete(root);
      }
  });
})();
